import { useNavigate } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete } from '@factorialco/f0-react/icons/app'
import { TrainingClassMembershipsDataCollectionDocument } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useLegalEntityFilter } from 'modules/employees/lib/filters/legalEntity'
import { useTeamFilter } from 'modules/employees/lib/filters/team'
import { useWorkplaceFilter } from 'modules/employees/lib/filters/workplace'
import { useDeleteTrainingClassMembership } from 'modules/trainings/hooks/TrainingClassMemberships/useDeleteTrainingClassMemberships'
import { DEFAULT_DATA_COLLECTION_STORAGE } from 'modules/trainings/lib/collections'

interface Props {
  classId: string
  canEdit: boolean
  id: string
}

export const ParticipantsTable = ({ id, classId, canEdit }: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { deleteTrainingClassMemberships } = useDeleteTrainingClassMembership()
  const teamFilter = useTeamFilter()
  const legalEntityFilter = useLegalEntityFilter()
  const workplaceFilter = useWorkplaceFilter()

  const handleBulkAction = (
    action: string,
    {
      itemsStatus,
    }: {
      itemsStatus: readonly { item: { id: string }; checked: boolean }[]
    }
  ) => {
    const selectedEmployeesIds = itemsStatus
      .filter((item) => item.checked)
      .map((item) => item.item.id)
    switch (action) {
      case 'delete-memberships':
        onDelete(selectedEmployeesIds)
        break
    }
  }

  const source = useGraphqlDataCollectionSource({
    document: TrainingClassMembershipsDataCollectionDocument,
    mapPagination: (data) => {
      return {
        total: data.trainings.trainingClassMembershipsConnection.totalCount ?? 0,
      }
    },
    mapData: (data) => data.trainings.trainingClassMembershipsConnection.nodes,
    search: {
      enabled: true,
    },
    filters: {
      team: teamFilter,
      legalEntity: legalEntityFilter,
      workplace: workplaceFilter,
    },
    presets: [],
    mapVariables: ({ filters, search }) => {
      const filterObject: Record<string, unknown> = {}

      if (filters.team && filters.team.length > 0) {
        filterObject.teamIds = { in: filters.team }
      }

      if (filters.legalEntity && filters.legalEntity.length > 0) {
        filterObject.legalEntityIds = { in: filters.legalEntity }
      }

      if (filters.workplace && filters.workplace.length > 0) {
        filterObject.locationIds = { in: filters.workplace }
      }

      return {
        trainingClassId: classId,
        search: search,
        filter: filterObject,
      }
    },
    selectable: (item) => item.id,
    primaryActions: () =>
      canEdit
        ? {
            label: i18n.t(
              'trainings.trainings_revamp.classes_sidepanel.participants.datatable.add'
            ),
            icon: Add,
            onClick: () => {
              navigate(
                navigation.trainings.list.details_revamp.view.trainingClasses.detail.participants.add.fullPath(
                  { id: String(id), classId: String(classId) }
                )
              )
            },
          }
        : undefined,
    itemActions: (item) =>
      [
        canEdit && {
          label: i18n.t('common.delete'),
          icon: Delete,
          critical: true,
          onClick: () => onDelete([String(item.id)]),
        },
      ].filter(Boolean),
    bulkActions: () => ({
      primary: [
        {
          id: 'delete-memberships',
          label: i18n.t('common.delete'),
          icon: Delete,
        },
      ],
    }),
  })

  const onDelete = async (ids: string[]) => {
    const result = await deleteTrainingClassMemberships({
      trainingClassId: classId,
      ids: ids,
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'trainings.trainings_revamp.classes_sidepanel.participants.datatable.error'
        ),
      })
    })
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t(
          'trainings.trainings_revamp.classes_sidepanel.participants.datatable.success_delete'
        ),
      })
    }
  }

  return (
    <OneDataCollection
      id="trainings/participants/v1"
      storage={DEFAULT_DATA_COLLECTION_STORAGE}
      source={source}
      onBulkAction={handleBulkAction}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t(
                  'trainings.trainings_revamp.classes_sidepanel.participants.datatable.name'
                ),
                render: (item) => ({
                  type: 'person',
                  value: {
                    firstName: item.access?.firstName ?? '',
                    lastName: item.access?.lastName ?? '',
                    name: item.access?.fullName ?? '',
                    type: 'person',
                    src: buildUrl(item.access?.avatar ?? '') ?? '',
                  },
                }),
              },
              {
                label: i18n.t(
                  'trainings.trainings_revamp.classes_sidepanel.participants.datatable.team'
                ),
                render: (item) => ({
                  type: 'text',
                  value: item.access?.employee?.memberships?.[0]?.team?.name ?? '',
                }),
              },
              {
                label: i18n.t(
                  'trainings.trainings_revamp.classes_sidepanel.participants.datatable.job_title'
                ),
                render: (item) => ({
                  type: 'text',
                  value: item.access?.employee?.referenceContract?.jobTitle ?? '',
                }),
              },
            ],
          },
        },
      ]}
    />
  )
}
